import type {
  InferenceEntry,
  PayeeBankCurrentValues,
  PayeeBankInferences,
} from '@/context/services/report_inferences/utils';
import {
  getMismatch,
  getPayeeBankMismatch,
} from '@/context/services/report_inferences/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

export type InferenceAlertState = {
  mismatch: InferenceEntry | null;
  onBlur: () => void;
};

export const useInferenceAlertState = (
  inference: InferenceEntry | undefined,
  getComparableValue: () => string | null | undefined,
  isAlertEnabled?: boolean
): InferenceAlertState => {
  const [mismatch, setMismatch] = useState<InferenceEntry | null>(null);

  const inferenceRef = useRef(inference);
  inferenceRef.current = inference;
  const isAlertEnabledRef = useRef(isAlertEnabled);
  isAlertEnabledRef.current = isAlertEnabled;
  const getComparableValueRef = useRef(getComparableValue);
  getComparableValueRef.current = getComparableValue;

  const compute = useCallback(() => {
    setMismatch(
      isAlertEnabledRef.current
        ? getMismatch(inferenceRef.current, getComparableValueRef.current())
        : null
    );
  }, []);

  // ① 初期render時のみ
  useEffect(() => {
    compute();
  }, [compute, inference, isAlertEnabled]);

  // ③ blur時: 最新の inference / getComparableValue を ref 経由で参照して再計算
  const onBlur = useCallback(() => {
    compute();
  }, [compute]);

  return { mismatch, onBlur };
};

export const usePayeeBankInferenceAlertState = (
  inferences: PayeeBankInferences,
  getCurrentValues: () => PayeeBankCurrentValues,
  isAlertEnabled?: boolean
): InferenceAlertState => {
  const [mismatch, setMismatch] = useState<InferenceEntry | null>(null);

  const inferencesRef = useRef(inferences);
  inferencesRef.current = inferences;
  const isAlertEnabledRef = useRef(isAlertEnabled);
  isAlertEnabledRef.current = isAlertEnabled;
  const getCurrentValuesRef = useRef(getCurrentValues);
  getCurrentValuesRef.current = getCurrentValues;

  const compute = useCallback(() => {
    setMismatch(
      isAlertEnabledRef.current
        ? getPayeeBankMismatch(
            inferencesRef.current,
            getCurrentValuesRef.current()
          )
        : null
    );
  }, []);

  useEffect(() => {
    compute();
  }, [
    compute,
    inferences.accountNumber,
    inferences.bankBranch,
    inferences.bankName,
    isAlertEnabled,
  ]);

  const onBlur = useCallback(() => {
    compute();
  }, [compute]);

  return { mismatch, onBlur };
};
